/** Cross-boundary vocabulary shared verbatim by src/server and frontend.
 *  Free-standing enums, not route responses — inert to the OpenAPI gate
 *  (only manifest-reachable schemas land in openapi.json). Each side builds
 *  its own Zod schemas / labels from these tuples and types. */

// Interactive (requires confirmations) vs Autonomous (auto-approves tools).
// Deprecated — superseded by VERBOSITY_LEVELS ('min' = autonomous, everything
// else = interactive). Kept through the transition window for old tabs and
// pre-migration runs; PR 2 of the verbosity unification deletes it.
export const EXECUTION_MODES = ['interactive', 'autonomous'] as const;
export type ExecutionMode = (typeof EXECUTION_MODES)[number];

// The single supervision knob. max/standard differ only in frontend display
// folding; 'min' is hands-off (confirmation gates off, spec judge on).
export const VERBOSITY_LEVELS = ['max', 'standard', 'min'] as const;
export type Verbosity = (typeof VERBOSITY_LEVELS)[number];

// Analysis Engine — which optical backend to use.
// - 'optiland': in-process Python analysis service
// - 'zemax': hosted Tailscale Windows worker pool (paid-per-seat; access via
//   admin or the per-user allow_zemax grant — see canUseHostedZemax)
// - 'zemax-byo': routed through the user's own bridge tab to their local
//   Zemax worker. Open to any authenticated user; bridge ownership is the gate.
// - 'codev': hosted CODE V worker pool (paid-per-seat; admin or the per-user
//   allow_codev grant — see canUseHostedCodev). Served by the native-only
//   CODE V worker and codev-analysis-service; a 'codev-byo' twin is reserved,
//   not added, until a bring-your-own CODE V bridge exists.
export const ANALYSIS_ENGINES = ['optiland', 'zemax', 'zemax-byo', 'codev'] as const;
export type AnalysisEngine = (typeof ANALYSIS_ENGINES)[number];

/** The tool / prompt / solve surface an engine exposes. Hosted and BYO Zemax
 *  differ only in where the worker runs, so they share one family. */
export const ENGINE_FAMILIES = ['optiland', 'zemax', 'codev'] as const;
export type EngineFamily = (typeof ENGINE_FAMILIES)[number];

/** Exhaustive by type: adding an engine without placing it in a family is a
 *  compile error here and in every other `Record<AnalysisEngine, …>` table. */
export const ENGINE_FAMILY: Record<AnalysisEngine, EngineFamily> = {
  optiland: 'optiland',
  zemax: 'zemax',
  'zemax-byo': 'zemax',
  codev: 'codev',
};

export function engineFamily(engine: AnalysisEngine): EngineFamily {
  return ENGINE_FAMILY[engine];
}

/** True for any engine that exposes the Zemax tool surface (hosted or BYO). */
export function isZemaxEngine(engine: AnalysisEngine): boolean {
  return engineFamily(engine) === 'zemax';
}

/** True for every engine served by an orchestrator + worker rather than the
 *  in-process analysis service — the ones that need bridge/callback headers. */
export function isExternalEngine(engine: AnalysisEngine): boolean {
  return engineFamily(engine) !== 'optiland';
}

/** The per-user grant a hosted engine needs, named as the boolean it appears
 *  under on the authenticated user / validation result; null = open to any
 *  authenticated user (optiland in-process, BYO gated by bridge ownership). */
export type HostedEngineGrant = 'canUseHostedZemax' | 'canUseHostedCodev';
export const HOSTED_ENGINE_GRANT: Record<AnalysisEngine, HostedEngineGrant | null> = {
  optiland: null,
  zemax: 'canUseHostedZemax',
  'zemax-byo': null,
  codev: 'canUseHostedCodev',
};
export type HostedEngineGrants = Record<HostedEngineGrant, boolean>;

/** The grant `engine` needs and `grants` lacks, or null when the caller may run it. */
export function engineGrantMissing(engine: AnalysisEngine, grants: HostedEngineGrants): HostedEngineGrant | null {
  const grant = HOSTED_ENGINE_GRANT[engine];
  return grant && !grants[grant] ? grant : null;
}

// Download formats of GET /systems/export: Zemax OpticStudio `.zmx` and CODE V
// `.seq`. The tuple order is the order the frontend's Export menu lists them.
export const SYSTEM_EXPORT_FORMATS = ['zmx', 'seq'] as const;
export type SystemExportFormat = (typeof SYSTEM_EXPORT_FORMATS)[number];
// The file extension per format, for the backend's Content-Disposition name and
// the frontend's download name.
export const SYSTEM_EXPORT_EXTENSIONS = {
  zmx: '.zmx',
  seq: '.seq',
} as const satisfies Record<SystemExportFormat, string>;
// Response header of GET /systems/export: how many conversion limitations the
// file lists (SEQ comments). The download
// is a bare blob, so the count is how the Export menu learns of them.
export const EXPORT_WARNING_COUNT_HEADER = 'X-Export-Warning-Count';

// Conversation/project access level
export const VISIBILITIES = ['private', 'company', 'everyone'] as const;
export type Visibility = (typeof VISIBILITIES)[number];

// The MTF/contrast members of the tolerancing criterion enum — the only
// HIGHER-is-better criteria: a larger contrast is a better build, and
// manufacturing perturbation degrades the criterion DOWNWARD (toward 0). Every
// other criterion (RMS spot/wavefront/angular error, BoresightError,
// MeritFunction) is LOWER-is-better. This is criterion polarity's single TS
// source — the agent tools (src/agent/tools/toleranceUtils.ts) and the
// tolerancing panel (frontend/tolerancing/api.ts) both derive from it, so the
// >= / <= sense the UI renders cannot drift from the flip the worker applies.
// Mirrored in Python by _MTF_CRITERIA in zemax_worker/zosapi_handler/
// tolerancing.py and zemax-analysis-service/routers/analysis_tolerancing.py;
// test_mtf_criterion_sets_match_across_services pins all three to this literal.
export const MTF_TOLERANCE_CRITERIA = [
  'GeometricMTFAverage', 'GeometricMTFSag', 'GeometricMTFTan',
  'DiffMTFAverage', 'DiffMTFSag', 'DiffMTFTan',
] as const;

/** True when a LARGER criterion value is a better build (the MTF/contrast
 *  criteria); false for the lower-is-better criteria (RMS*, BoresightError,
 *  MeritFunction). Drives yield-tail selection, worst-offender polarity, and
 *  whether a spatial-frequency (mtf_frequency) input applies. */
export function criterionHigherIsBetter(criterion: string): boolean {
  return (MTF_TOLERANCE_CRITERIA as readonly string[]).includes(criterion);
}

// The tolerancing criteria both tolerance tools accept — native
// ZOSAPI.Tools.Tolerancing.Criterions member names. The worker validates against
// the live enum, so this is the common-member subset the tools expose, not the
// whole enum. Single source for the two tool schemas' z.enum() and for
// CRITERION_UNITS below.
export const TOLERANCE_CRITERIA = [
  'RMSSpotRadius', 'RMSWavefront', 'RMSSpotX', 'RMSSpotY',
  'RMSAngularRadius', 'RMSAngularX', 'RMSAngularY', 'BoresightError',
  'GeometricMTFAverage', 'GeometricMTFSag', 'GeometricMTFTan',
  'DiffMTFAverage', 'DiffMTFSag', 'DiffMTFTan', 'MeritFunction',
] as const;

export type ToleranceCriterion = (typeof TOLERANCE_CRITERIA)[number];

/** The DIFFRACTION-MTF members of TOLERANCE_CRITERIA — Average / Sag / Tan.
 *  Selected by the `DiffMTF` prefix, which is how the native Criterions enum
 *  spells them, so a diffraction-MTF member added to TOLERANCE_CRITERIA lands
 *  here without a second edit; intersected with TOLERANCE_CRITERIA rather than
 *  testing the prefix alone, so a name neither tool accepts never classifies.
 *  The GEOMETRIC MTF criteria are a different family and are not in it. */
export const DIFFRACTION_MTF_TOLERANCE_CRITERIA: ReadonlySet<string> = new Set(
  TOLERANCE_CRITERIA.filter((criterion) => criterion.startsWith('DiffMTF')),
);

/** True for the diffraction-MTF criteria. The sole implementation — the agent
 *  tools (src/agent/tools/generateToleranceBrief.ts) and the tolerancing panel
 *  (frontend/tolerancing/api.ts) both import it from here, so the two sides
 *  cannot classify a criterion differently. */
export function isDiffractionMtfCriterion(criterion: string): boolean {
  return DIFFRACTION_MTF_TOLERANCE_CRITERIA.has(criterion);
}

/** The criterion that scores against the lens's own merit function rather than
 *  one OpticStudio builds from the Criterion / Field / MTF Frequency / Sampling
 *  settings (textbooks/ZemaxManual/auto/ZemaxManual.md:15619): "Merit Function:
 *  Uses whatever merit function has been defined for the lens" (:15528). The
 *  .zmx the worker loads carries no merit operands, so this criterion is the one
 *  that needs the conversation's merit rows sent alongside the tolerance rows.
 *  Typed to the union so renaming the member is a compile error rather than a
 *  silently-dead branch. */
export const MERIT_FUNCTION_CRITERION: ToleranceCriterion = 'MeritFunction';

// Display unit per criterion, for the relayed interpretation and the ME-facing
// tolerance brief (the structured stats are unit-agnostic). Keyed on the
// criterion union so it is EXHAUSTIVE: adding a member to TOLERANCE_CRITERIA
// without a unit is a compile error, rather than a run rendering "(units)" into
// agent prose and into a manufacturing document (#3442).
//
// Grounding — ZEMAX manual, Tolerancing > Criterion:
//  - BoresightError "is defined as the radial chief ray coordinate traced for
//    the on axis field divided by the effective focal length … always computed
//    at the primary wavelength in radians". Live-probed: with a 0.3° coordinate
//    -break tilt ahead of a singlet, BSER = 0.0052134 = |chief ray radial
//    coordinate at the image| / EFL exactly — a length/length ratio, so radians.
//  - The RMS Angular criteria are "based upon the direction cosines of the
//    output rays" — also dimensionless. Live-probed on a 20 mm-EPD / 97.4 mm-EFL
//    singlet: nominal RMSAngularRadius = 0.0731, matching the ray-cone direction
//    -cosine spread in radians (milliradians would have read ~73).
//  - MeritFunction is a weighted RMS over operands with mixed units, so no unit
//    applies to it; it is labeled by the quantity instead. Live-probed on the
//    same singlet with a two-row MFE (EFFL target 100 + RSRE target 0 — mm and
//    mm, but combined through the weighted RMS): criterion nominal 1.82024 ==
//    MFE.CalculateMeritFunction() 1.8202352, i.e. the merit value itself.
//  - The spot criteria are LENGTHS in lens units, the wavefront one is in WAVES.
//    Separated by wavelength, not by name: doubling the system wavelength
//    0.55 → 1.10 µm on that singlet moved nominal RMSSpotRadius 0.028013 →
//    0.027302 (unchanged, so a length) while nominal RMSWavefront went
//    0.769525 → 0.366003 (ratio 0.476 ≈ ½, so a count of waves of the system's
//    own wavelength). RMSSpotX/Y read 0.019808 = 0.028013/√2, the X/Y
//    decomposition of the same length; RMSAngularX/Y likewise 0.051664 =
//    0.073064/√2.
//  - The MTF criteria are dimensionless contrast in [0, 1]: probed at 20 cy/mm,
//    Geometric MTF Average/Sag/Tan 0.124571 and Diff MTF Average/Sag/Tan
//    0.011708 / 0.010482 / 0.011708.
// 'lens units' stays literal. The ME-facing brief substitutes the system's
// actual unit, but the tolerance run payload carries no lens unit for the
// tolerancing panel to substitute from, so on a non-mm system the panel's label
// reads generic rather than wrong.
export const CRITERION_UNITS: Record<ToleranceCriterion, string> = {
  RMSSpotRadius: 'lens units', RMSSpotX: 'lens units', RMSSpotY: 'lens units',
  RMSWavefront: 'waves',
  RMSAngularRadius: 'rad', RMSAngularX: 'rad', RMSAngularY: 'rad',
  BoresightError: 'rad',
  GeometricMTFAverage: 'contrast', GeometricMTFSag: 'contrast', GeometricMTFTan: 'contrast',
  DiffMTFAverage: 'contrast', DiffMTFSag: 'contrast', DiffMTFTan: 'contrast',
  MeritFunction: 'merit value',
};

/** Printed where a compensator readout's own unit is not knowable from what
 *  that readout carries.
 *
 *  A compensator's values are in the units of the quantity its TDE row frees,
 *  and that is not one unit: COMP's Param2 code selects thickness (lens units),
 *  curvature — 1 over the radius — (1/lens units) or conic (dimensionless)
 *  (src/data/agent-zemax-tolerance-operands.json, COMP.paramCodeMeanings and
 *  its manual `notes`: "Int2 = 0, compensator is thickness; Int2 = 1,
 *  compensator is curvature (1 over the radius); Int2 = 2, compensator is
 *  conic"), while CPAR/CNPA/CNPS/CMCO free a surface parameter, a
 *  non-sequential object property or a multi-configuration operand whose
 *  dimension follows the thing freed.
 *
 *  So a reader who is shown a compensator value and no unit reads a length —
 *  these are ME-facing travel numbers. A readout that cannot name the quantity
 *  says that, rather than letting the default assumption stand.
 *
 *  Shared so the ME brief and the tolerancing panel say the same thing about
 *  the same number (#4515). */
export const UNRESOLVED_COMPENSATOR_UNIT = '(unit unresolved)';

/** The surfaces an offender's tolerance actually perturbs: `surfaces` minus
 *  `adjust_surface`, which takes up the error rather than being toleranced by
 *  that row. TTHI's disable form names the same surface in both slots, where it
 *  IS perturbed and must survive.
 *  The sole implementation — the Node backend (the agent's offender lines and
 *  the ME brief) and the frontend driver table both import it from here. It
 *  labels offender rows and feeds no arithmetic. Its behaviour is pinned by
 *  tests/fixtures/perturbed-surfaces-parity-cases.json, replayed by
 *  tests/agent/tools/perturbedSurfacesParity.test.ts — add cases to the
 *  fixture. Render anything else and the agent and the panel name different
 *  surfaces for one row. */
export function perturbedSurfaces(o: { surfaces: number[]; adjust_surface: number | null }): number[] {
  const surfaces = o.adjust_surface !== null && o.surfaces.filter((s) => s === o.adjust_surface).length === 1
    ? o.surfaces.filter((s) => s !== o.adjust_surface)
    : o.surfaces;
  return [...new Set(surfaces)];
}

// The tolerancing criteria that do not support Separate Fields/Configs
// (ITolerancing.IsSeperateFieldConfigurationsUsed). Zemax manual, Criterion tab:
// the Merit Function and User Script entries each end "Separate Fields/Configs
// is not supported when using this criterion."; no other listed criterion
// carries the restriction. UserScript stays in the set though no TypeScript
// caller offers it, so the rule is keyed on the manual rather than on which
// members happen to be exposed.
// Mirrored by _SEPARATE_FIELDS_UNSUPPORTED_CRITERIA in
// zemax_worker/zosapi_handler/tolerancing.py, which holds the enum sweep behind
// it; tests/tools/separateFieldsCriteriaParity.test.ts pins it against this.
export const SEPARATE_FIELDS_UNSUPPORTED_CRITERIA: ReadonlySet<string> = new Set([
  MERIT_FUNCTION_CRITERION, 'UserScript',
]);

/** Operand codes whose Monte-Carlo column repeats one value every trial instead
 *  of that trial's draw, mapped to what that value is. Zemax still redraws the
 *  row's Zernike coefficients each trial (§7.2.1.1.16/17), so a constant column
 *  does not mean the row went unsampled. TEZI's value is its Max, the "exact RMS
 *  tolerance" the coefficients are normalized to (§7.2.1.1.17); TEXI's is the
 *  manual's c (src/data/agent-zemax-tolerance-operands.json, TEXI), a Zernike
 *  Fringe Sag coefficient, whose units "are the same as the lens units". Probed
 *  on 25.2 SP4, three samples and surface types:
 *  zemax-scripts/tolerancing_mc_fixed_magnitude_tezi_texi_*.py →
 *  zemax-outputs/tolerancing-mc-fixed-magnitude-tezi-texi-*.json.
 *  Shared so the agent's mc_operands caveat and the tolerancing panel's
 *  footnote name the same codes. */
export const FIXED_MAGNITUDE_MC_COLUMNS: ReadonlyMap<string, string> = new Map([
  ['TEZI', "the row's RMS surface error, equal to its Max"],
  ['TEXI', "the per-term Zernike Fringe Sag coefficient c = λ·f/(2√n) in lens units, where λ is the test wavelength in lens units, f the row's Max in fringes and n = Max# − Min# + 1 terms — not fringes, and not the Max itself"],
]);

// Models the agent stack can run on. PROXY_AGENT_MODELS are LiteLLM
// `model_name`s (must stay in sync with litellm/config.yaml, the proxy's
// single config — asserted by tests/config/agentModels.test.ts); they are
// selectable only when LITELLM_BASE_URL is configured. Every id here must be
// a 1M-context model: the backend declares a 1M window to the Agent SDK CLI
// for any foreign model (src/config/modelRouting.ts, proxyEnv) and
// MAX_ATTACHMENT_PDF_PAGES=600 (src/agent/agentReadableFiles.ts) assumes it.
export const CLAUDE_AGENT_MODELS = [
  'claude-opus-5-5',
  'claude-opus-5',
  'claude-opus-4-8',
  'claude-opus-4-7',
  'claude-fable-5-1',
  'claude-fable-5',
] as const;

export const PROXY_AGENT_MODELS = [
  'gpt-6-astra',
  'gpt-5.6',
  'deepseek-v4-pro',
  'deepseek-v4-flash',
  'kimi-k3',
  'kimi-k2.6',
] as const;

export const AGENT_MODELS = [...CLAUDE_AGENT_MODELS, ...PROXY_AGENT_MODELS] as const;
export type AgentModel = (typeof AGENT_MODELS)[number];
export type ProxyAgentModel = (typeof PROXY_AGENT_MODELS)[number];

// Merit operands whose "Op#" param is NOT a row of the merit function being
// edited, so it must never be index-translated, re-pointed when rows move, or
// cleared as a dangling row reference. Imported by both the agent path
// (src/agent/tools/resolveNamedParams.ts) and the merit editor
// (frontend/merit-function/operandRegistry.ts); the worker keeps a Python copy
// (_NON_MFE_OP_REFERENCE_OPERANDS in zemax_worker/zosapi_handler/optimization.py
// — the standalone worker repo cannot import TS) pinned equal by
// tests/tools/resolveNamedParams.test.ts.
//
// - MCOG/MCOL/MCOV: "Op# defines which multi-configuration operand is used"
//   (manual 2026 R1) — a Multi-Configuration Editor row. Live-verified
//   2026-07-20: MCOV Op#=2 in a 3-row MFE returns MCE row 2's value.
// - CMFV: "an integer which defines the operand row # from which to extract the
//   value from ... merit function operand 7 in construction file 2" (manual
//   2026 R1) — a row in a DIFFERENT system's merit function.
//
// GOTO is deliberately absent: its Op# is an MFE row — "Execution of the merit
// function will start again at the Op# line" (manual 2026 R1).
export const NON_MFE_OP_REFERENCE_OPERANDS: ReadonlySet<string> = new Set([
  'MCOG', 'MCOL', 'MCOV', 'CMFV',
]);

// Per-child run state of a spawned branch agent: classified by
// src/services/conversations/childAgents.ts, rendered by the branch banner and
// the design-tree views.
export type ChildRunState =
  | 'not_started'
  | 'queued'
  | 'running'
  | 'complete'
  | 'completed_without_success'
  | 'errored'
  | 'kickoff_skipped'
  | 'orphaned'
  | 'cancelled';

/** States with no live run and no usable result — the only states
 *  POST /conversations/:conversation_id/respawn-agent accepts; a re-spawn re-runs
 *  the child's stored kickoff instruction without discarding anything.
 *  'kickoff_skipped' is deliberately excluded: it means the USER opened the
 *  child and ran their own turn (that turn is what beat the kickoff to the
 *  lease), so the child holds a real session with user context — a respawn
 *  would fork a fresh session from the parent and silently discard it. */
export const RESPAWNABLE_CHILD_RUN_STATES: ReadonlySet<ChildRunState> = new Set([
  'errored', 'orphaned', 'cancelled', 'completed_without_success',
]);

/** How much a conversion warning asks of its reader: `action` (do something, or the result is for another
 *  system), `changed` (the import/export altered the design) or `info` (the engine's normal behaviour, a
 *  hedge, an immaterial conversion). The converter's WARNING_CODE_LEVELS (zemax-converter/src/normalizer.ts)
 *  assigns one per warning code; it restates these literals because it builds before this package. */
export const WARNING_LEVELS = ['action', 'changed', 'info'] as const;
export type WarningLevel = typeof WARNING_LEVELS[number];
